import { supabase } from "./supabase";
import {
  janelaDeInscricao,
  remuneracaoBase,
  situacaoDaInscricao,
  vagasImediatas,
  hojeIso,
  type Janela,
  type Situacao,
} from "./concursos";

// O IO do Mapa de Concursos Contábeis (000120).
//
// Este módulo transporta e DERIVA — a derivação usando `concursos.ts`, que é o
// módulo puro com prova própria. Quem decide se o aluno pode ler é a RLS
// (`conteudo_liberado('mapa-de-concursos') or eh_admin()`); quem decide o que
// aparece é `MapaConcursos.tsx`.
//
// ---------------------------------------------------------------------------
// O catálogo vem INTEIRO, numa ida
// ---------------------------------------------------------------------------
// São 256 linhas (medido em 27/08/2026), e o desenho é o de
// `mei_ocupacoes_lista`: lista inteira de uma vez, filtro local. A razão não é
// preguiça — é que tudo o que a tela mostra é agregado sobre o conjunto TODO:
//
//   - o mapa pinta 27 estados pela contagem de inscrições ABERTAS em cada um;
//   - a cabine diz quantas estão abertas, quantas vagas imediatas somam e
//     quantas fecham nos próximos 7 dias;
//   - os quatro estados da situação são contados para os chips de filtro.
//
// Paginar obrigaria a tela a pedir esses agregados em consultas separadas, e aí
// existiriam duas verdades sobre o mesmo número — o defeito de "agregar acesso
// em JavaScript" com outra roupa.
//
// ---------------------------------------------------------------------------
// A SITUAÇÃO é derivada aqui, e não vem do banco
// ---------------------------------------------------------------------------
// `mc_concursos` guarda os FATOS (`inscricao_inicio`, `inscricao_fim`) e nada
// mais. A situação sai de `situacaoDaInscricao`, sobre a data de HOJE do relógio
// de quem olha.
//
// É a doutrina do `mei_teto`: guarda os fatos e deriva o número, e o dia seguinte
// se corrige sozinho, sem evento nenhum. Na origem o status é GRAVADO, e ele
// mentia em 218 das 256 linhas — o banco dizia 250 abertas, a tela mostrava 50 e
// os fatos diziam 32.
//
// E a derivação NÃO é repetida em plpgsql: seriam duas definições da mesma
// regra, que é o que a `000109` teve de remover (cinco funções de data no banco
// com zero chamadas).

/** Uma linha do catálogo, já com o que a tela precisa derivado. */
export type Concurso = {
  id: string;
  orgao: string;
  cargo: string;
  uf: string;
  cidade: string | null;
  /** Só quando ACRESCENTA algo a cidade/UF — a carga não grava a cópia derivável. */
  localProva: string | null;
  banca: string | null;
  vagasTexto: string | null;
  /** Derivado: "CR" (cadastro de reserva) é zero vaga imediata. */
  vagasImediatas: number;
  remuneracaoTexto: string | null;
  /** Derivado do texto pela carga; é ele que ordena e filtra. */
  remuneracao: number | null;
  jornada: string | null;
  /** O texto original, que carrega as HORAS que a data não guarda. */
  inscricaoTexto: string | null;
  janela: Janela | null;
  situacao: Situacao;
  dataProva: string | null;
  linkEdital: string | null;
  observacoes: string | null;
  /** Quando alguém olhou este concurso. Vai para a TELA junto do dado. */
  conferidoEm: string | null;
  favorito: boolean;
};

type LinhaDoCatalogo = {
  id: string;
  orgao: string;
  cargo: string;
  uf: string;
  cidade: string | null;
  local_prova: string | null;
  banca: string | null;
  vagas_texto: string | null;
  remuneracao_texto: string | null;
  remuneracao_base: number | string | null;
  jornada: string | null;
  inscricao_texto: string | null;
  inscricao_inicio: string | null;
  inscricao_fim: string | null;
  data_prova: string | null;
  link_edital: string | null;
  observacoes: string | null;
  conferido_em: string | null;
  favorito: boolean;
};

/**
 * O catálogo ativo, com o favorito do aluno marcado e a situação derivada.
 *
 * Devolve `null` quando a leitura falha — a tela mostra a mensagem de erro. Erro
 * de LEITURA é diferente de erro de escrita: sem leitura não há tela, e é por
 * isso que `favoritar` abaixo não usa este caminho (a cicatriz de 25/08 do
 * Calendário, onde um tique que não gravou trocava o mês inteiro pela mensagem
 * "não conseguimos carregar").
 */
export async function lerCatalogo(hoje = hojeIso(new Date())): Promise<Concurso[] | null> {
  const { data, error } = await supabase.rpc("mc_catalogo");
  if (error || !data) return null;

  return (data as unknown as LinhaDoCatalogo[]).map((c) => {
    // A janela vem das DUAS colunas, e não do texto: quem já leu o texto foi a
    // carga (ou a tela de admin), com a mesma função. Reler aqui seria a
    // terceira derivação da mesma coisa — o defeito da origem.
    //
    // As duas juntas ou as duas nulas: a constraint do banco garante, e é por
    // isso que basta testar uma.
    const janela =
      c.inscricao_inicio && c.inscricao_fim
        ? { inicio: c.inscricao_inicio, fim: c.inscricao_fim }
        : null;

    return {
      id: c.id,
      orgao: c.orgao,
      cargo: c.cargo,
      uf: c.uf,
      cidade: c.cidade,
      localProva: c.local_prova,
      banca: c.banca,
      vagasTexto: c.vagas_texto,
      vagasImediatas: vagasImediatas(c.vagas_texto),
      remuneracaoTexto: c.remuneracao_texto,
      remuneracao: c.remuneracao_base === null ? null : Number(c.remuneracao_base),
      jornada: c.jornada,
      inscricaoTexto: c.inscricao_texto,
      janela,
      situacao: situacaoDaInscricao(janela, hoje),
      dataProva: c.data_prova,
      linkEdital: c.link_edital,
      observacoes: c.observacoes,
      conferidoEm: c.conferido_em,
      favorito: c.favorito,
    };
  });
}

/**
 * Marca ou desmarca um concurso.
 *
 * Devolve a mensagem de erro do banco, ou `null` quando deu certo — o padrão de
 * `marcarMeta`. Quem chama decide o que fazer com a falha, e a decisão certa é
 * avisar SÓ na peça que falhou: um favorito que não gravou não pode trocar o
 * catálogo inteiro por uma tela de erro.
 */
export async function favoritar(concursoId: string, favorito: boolean): Promise<string | null> {
  const { error } = await supabase.rpc("mc_favoritar", {
    p_concurso: concursoId,
    p_favorito: favorito,
  });
  return error ? error.message : null;
}

// ===========================================================================
// O lado do ADMIN
// ===========================================================================

/** Uma linha da tela de administração — arquivados incluídos. */
export type ConcursoAdmin = Concurso & {
  origemRef: string | null;
  conferidoPorNome: string | null;
  ativo: boolean;
  /**
   * Quantos alunos marcaram este concurso.
   *
   * Vai para a tela de propósito: arquivar um concurso que 12 alunos acompanham
   * é uma decisão diferente de arquivar um que ninguém olhou, e sem o número na
   * frente ela é tomada às cegas.
   */
  favoritos: number;
};

type LinhaAdmin = LinhaDoCatalogo & {
  origem_ref: string | null;
  conferido_por_nome: string | null;
  ativo: boolean;
  favoritos: number;
};

export async function lerCatalogoAdmin(
  hoje = hojeIso(new Date()),
): Promise<ConcursoAdmin[] | null> {
  const { data, error } = await supabase.rpc("mc_catalogo_admin");
  if (error || !data) return null;

  return (data as unknown as LinhaAdmin[]).map((c) => {
    const janela =
      c.inscricao_inicio && c.inscricao_fim
        ? { inicio: c.inscricao_inicio, fim: c.inscricao_fim }
        : null;
    return {
      id: c.id,
      origemRef: c.origem_ref,
      orgao: c.orgao,
      cargo: c.cargo,
      uf: c.uf,
      cidade: c.cidade,
      localProva: c.local_prova,
      banca: c.banca,
      vagasTexto: c.vagas_texto,
      vagasImediatas: vagasImediatas(c.vagas_texto),
      remuneracaoTexto: c.remuneracao_texto,
      remuneracao: c.remuneracao_base === null ? null : Number(c.remuneracao_base),
      jornada: c.jornada,
      inscricaoTexto: c.inscricao_texto,
      janela,
      situacao: situacaoDaInscricao(janela, hoje),
      dataProva: c.data_prova,
      linkEdital: c.link_edital,
      observacoes: c.observacoes,
      conferidoEm: c.conferido_em,
      conferidoPorNome: c.conferido_por_nome,
      ativo: c.ativo,
      favoritos: c.favoritos,
      favorito: false,
    };
  });
}

/** O que a tela de admin manda para o banco. */
export type Rascunho = {
  id: string | null;
  orgao: string;
  cargo: string;
  uf: string;
  cidade: string;
  localProva: string;
  banca: string;
  vagasTexto: string;
  remuneracaoTexto: string;
  jornada: string;
  inscricaoTexto: string;
  dataProva: string;
  linkEdital: string;
  observacoes: string;
};

/**
 * Grava o concurso, DERIVANDO a janela e o número da remuneração dos textos.
 *
 * A derivação é aqui, na fronteira com o servidor, e não na tela: é o mesmo
 * corte de "guarde as duas coisas separadas (o texto que aparece e o número que
 * viaja), converta na FRONTEIRA com o servidor — um lugar por onde todo campo
 * passa, nunca na tela". Assim a tela de admin não pode esquecer de converter, e
 * não existe um segundo campo de número para o operador digitar (que é o defeito
 * da origem: 7 das 256 linhas com o número divergindo do texto).
 *
 * O carimbo de quem conferiu e quando é do SERVIDOR — nem chega a viajar.
 */
export async function salvarConcurso(r: Rascunho): Promise<{ id: string | null; erro: string | null }> {
  const janela = janelaDeInscricao(r.inscricaoTexto);
  const { data, error } = await supabase.rpc("mc_salvar_concurso", {
    p_id: r.id,
    p_orgao: r.orgao,
    p_cargo: r.cargo,
    p_uf: r.uf,
    p_cidade: r.cidade,
    p_local_prova: r.localProva,
    p_banca: r.banca,
    p_vagas_texto: r.vagasTexto,
    p_remuneracao_texto: r.remuneracaoTexto,
    p_remuneracao_base: remuneracaoBase(r.remuneracaoTexto),
    p_jornada: r.jornada,
    p_inscricao_texto: r.inscricaoTexto,
    p_inscricao_inicio: janela ? janela.inicio : null,
    p_inscricao_fim: janela ? janela.fim : null,
    p_data_prova: r.dataProva || null,
    p_link_edital: r.linkEdital,
    p_observacoes: r.observacoes,
  });
  if (error) return { id: null, erro: error.message };
  return { id: (data as unknown as string) ?? null, erro: null };
}

export async function arquivarConcurso(id: string, arquivar: boolean): Promise<string | null> {
  const { error } = await supabase.rpc("mc_arquivar_concurso", {
    p_id: id,
    p_arquivar: arquivar,
  });
  return error ? error.message : null;
}

/**
 * O id da conta de quem está olhando — SÓ para namespear chave de
 * `localStorage`.
 *
 * Não serve para decidir nada: quem decide acesso é a RLS, e as RPCs nunca
 * recebem id de aluno. Existe porque a dispensa do alerta de prazo vale por
 * aluno e por dia, e no app de origem a chave equivalente é GLOBAL
 * (`first_visit_popup_seen`) — com dois alunos no mesmo navegador, o segundo
 * nunca vê o aviso.
 *
 * `getUser()` lê a sessão que já está no navegador; não é ida à rede.
 */
export async function idDaSessao(): Promise<string> {
  const { data } = await supabase.auth.getUser();
  return data.user?.id ?? "anon";
}
